import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useAuth, OAuthProvider } from '../../contexts/AuthContext';
import { API_CONFIG, API_ENDPOINTS } from '../../utils/constants';
import { usePostHog } from 'posthog-js/react';
import Portal from '../Portal';
import { suggestEmailCorrection } from '../../utils/emailTypoCheck';

// Hardcoded blocklist of known disposable email domains (Solution 1 — instant, no network)
const BLOCKED_EMAIL_DOMAINS = new Set([
  'bvxmail.com',
  'dev.nondon.store',
  'mailslurp.biz',
  'tempsmtp.com',
  'dollicons.com',
  'homuno.com',
  'manupay.com',
  'muhaos.com',
  'newtrea.com',
  'web-library.net',
  'znvx.me',
  'zelnro.com',
  'inboxopen.space',
  'inboxarea.xyz',
  'mailinator.com',
  'guerrillamail.com',
  'guerrillamail.net',
  'guerrillamail.org',
  'tempmail.com',
  'throwaway.email',
  'temp-mail.org',
  'fakeinbox.com',
  'sharklasers.com',
  'guerrillamailblock.com',
  'grr.la',
  'dispostable.com',
  'yopmail.com',
  'yopmail.fr',
  'trashmail.com',
  'trashmail.me',
  'trashmail.net',
  'maildrop.cc',
  'mailnesia.com',
  'tempail.com',
  'tempr.email',
  'discard.email',
  'discardmail.com',
  'mohmal.com',
  'getnada.com',
  'emailondeck.com',
  'harakirimail.com',
  'tmail.ws',
]);

// Blocked domain suffixes — matches the domain itself or any subdomain ending in it.
// edu.pl (Polish academic TLD) is being abused at scale for free-credit farming; we
// accept losing legitimate edu.pl users in exchange for blocking the abuse.
const BLOCKED_EMAIL_DOMAIN_SUFFIXES = [
  'edu.pl',
];

export interface AuthModalProps {
  onClose: () => void;
  onEmailSent: (email: string) => void;
  actionName: string;
  hideSignInToggleAndCancel?: boolean; // Hide sign-in toggle and cancel button (for /lander-1)
}

const oauthProviders: Array<{ provider: OAuthProvider; label: string; icon: JSX.Element }> = [
  {
    provider: 'google',
    label: 'Continue with Google',
    icon: (
      <svg className="w-5 h-5" viewBox="0 0 24 24">
        <path
          fill="#4285F4"
          d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
        />
        <path
          fill="#34A853"
          d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
        />
        <path
          fill="#FBBC05"
          d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
        />
        <path
          fill="#EA4335"
          d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
        />
      </svg>
    ),
  },
];

export default function AuthModal({ onClose, onEmailSent, actionName, hideSignInToggleAndCancel = false }: AuthModalProps) {
  const { signInWithOAuth } = useAuth();
  const posthog = usePostHog();
  const [email, setEmail] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [oauthLoading, setOauthLoading] = useState<OAuthProvider | null>(null);
  const [authMode, setAuthMode] = useState<'signup' | 'signin'>('signup');
  const [attemptedMethod, setAttemptedMethod] = useState<'google' | 'email' | 'none'>('none');
  const modalOpenedAtRef = useRef(Date.now());
  const isFacebookBrowser = useRef(/FBAN|FBAV/.test(navigator.userAgent)).current;

  // Task 4 (v/signup-may5): typo-correction suggestion. Recomputed on every
  // email change. Tracking effect below fires the PostHog "shown" event at
  // most once per distinct suggested correction (so we don't double-count
  // every keystroke).
  const typoSuggestion = useMemo(() => suggestEmailCorrection(email), [email]);
  const lastTrackedSuggestionRef = useRef<string | null>(null);

  // Hide Google OAuth button for iOS + Facebook/Instagram in-app browsers.
  // Google blocks its OAuth consent screen in embedded WebViews (disallowed_useragent),
  // forcing a bounce to Safari that often fails to redirect back — causing "orphaned"
  // signups (server-side account created, client never returns). Email/OTP stays in
  // the WebView and has a proven 100% success rate for this population.
  // Matches ONLY iOS + Meta in-app browsers; all other combos keep the Google button.
  const hideGoogleAuth = useRef(
    /iPhone|iPad|iPod/.test(navigator.userAgent) &&
    /FBAN|FBAV|Instagram/.test(navigator.userAgent)
  ).current;

  // Track modal view on mount
  useEffect(() => {
    posthog.capture('auth_modal_view', {
      default_mode: 'signup',
      is_facebook_browser: isFacebookBrowser,
      hide_google_auth: hideGoogleAuth,
      action_name: actionName,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Lock body scroll when modal is open
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = '';
    };
  }, []);

  // Task 4: PostHog tracking — fire 'email_typo_suggestion_shown' once per
  // distinct suggestion. Resets when the user accepts/dismisses the
  // suggestion (typoSuggestion goes null) so a subsequent typo on the same
  // input is tracked again.
  useEffect(() => {
    if (typoSuggestion) {
      const key = `${typoSuggestion.originalDomain}->${typoSuggestion.suggestedDomain}`;
      if (lastTrackedSuggestionRef.current !== key) {
        lastTrackedSuggestionRef.current = key;
        posthog.capture('email_typo_suggestion_shown', {
          original_domain: typoSuggestion.originalDomain,
          suggested_domain: typoSuggestion.suggestedDomain,
          reason: typoSuggestion.reason,
        });
      }
    } else {
      lastTrackedSuggestionRef.current = null;
    }
  }, [typoSuggestion, posthog]);

  // Task 4: accept the suggestion. Click handler updates the email input
  // to the corrected value and emits the 'accepted' analytics event.
  const acceptTypoSuggestion = () => {
    if (!typoSuggestion) return;
    posthog.capture('email_typo_suggestion_accepted', {
      original_domain: typoSuggestion.originalDomain,
      suggested_domain: typoSuggestion.suggestedDomain,
      reason: typoSuggestion.reason,
    });
    setEmail(typoSuggestion.suggestion);
  };

  const validateEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleOAuthSignIn = async (provider: OAuthProvider) => {
    try {
      setError(null);
      setAttemptedMethod('google');
      setOauthLoading(provider);
      posthog.capture('auth_modal_google_clicked', {
        is_facebook_browser: isFacebookBrowser,
      });
      await signInWithOAuth(provider);
      // OAuth redirect will happen automatically via Supabase
    } catch (err) {
      posthog.capture('auth_modal_google_failed', {
        is_facebook_browser: isFacebookBrowser,
        error: err instanceof Error ? err.message : String(err),
      });
      setError(err instanceof Error ? err.message : 'Failed to sign in');
      setOauthLoading(null);
    }
  };

  const isDisposableEmail = (email: string): boolean => {
    const domain = email.split('@')[1]?.toLowerCase();
    if (!domain) return false;
    if (BLOCKED_EMAIL_DOMAINS.has(domain)) return true;
    return BLOCKED_EMAIL_DOMAIN_SUFFIXES.some(
      (suffix) => domain === suffix || domain.endsWith(`.${suffix}`)
    );
  };

  const handleEmailContinue = async () => {
    setError(null);

    if (!email.trim()) {
      setError('Please enter your email address');
      return;
    }

    if (!validateEmail(email)) {
      setError('Please enter a valid email address');
      return;
    }

    // Solution 1: Hardcoded blocklist check (instant, no network)
    if (isDisposableEmail(email)) {
      posthog.capture('auth_modal_email_blocked', {
        domain: email.split('@')[1]?.toLowerCase(),
        check_type: 'client_blocklist',
        is_facebook_browser: isFacebookBrowser,
      });
      setError('Please use a permanent email address. Temporary or disposable emails are not allowed.');
      return;
    }

    setAttemptedMethod('email');
    posthog.capture('auth_modal_email_submitted', {
      is_facebook_browser: isFacebookBrowser,
      email_domain: email.split('@')[1]?.toLowerCase(),
    });

    setLoading(true);

    try {
      // Solution 2: Backend API validation (comprehensive, 5000+ domains)
      try {
        const validateResp = await fetch(
          `${API_CONFIG.BASE_URL}${API_ENDPOINTS.AUTH.VALIDATE_EMAIL}`,
          {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ email }),
          }
        );

        if (validateResp.ok) {
          const result = await validateResp.json();
          if (!result.valid) {
            posthog.capture('auth_modal_email_blocked', {
              domain: email.split('@')[1]?.toLowerCase(),
              check_type: 'server_api',
              is_facebook_browser: isFacebookBrowser,
            });
            setError('Please use a permanent email address. Temporary or disposable emails are not allowed.');
            setLoading(false);
            return;
          }
        }
        // If response is not ok, fall through — don't block legitimate users
      } catch {
        // API unreachable — fall through, hardcoded blocklist already caught known domains
      }

      const { getSupabaseClient } = await import('../../services/supabase');
      const supabase = getSupabaseClient();
      const redirectTo = `${window.location.origin}/auth/callback`;

      const { error } = await supabase.auth.signInWithOtp({
        email,
        options: {
          shouldCreateUser: authMode === 'signup',
          emailRedirectTo: redirectTo,
        },
      });

      if (error) {
        setError(error.message || 'Failed to send verification code. Please try again.');
        setLoading(false);
        return;
      }

      // Success - close this modal and open OTP modal
      onEmailSent(email);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to send verification code');
      setLoading(false);
    }
  };

  return (
    <Portal>
      <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[9999] p-4 overflow-y-auto">
        <div className="bg-bg-panel border border-border rounded-modal p-4 sm:p-6 md:p-8 w-full max-w-[min(450px,100%)] my-auto">
          <h2 className="text-h1 font-medium text-white mb-3 sm:mb-4">
            {authMode === 'signup' ? 'Create An Account' : 'Sign In'}
          </h2>
          <p className="text-body text-text-muted mb-4 sm:mb-6">
            {authMode === 'signup'
              ? 'Your first video is free.'
              : 'Sign in to continue.'}
          </p>

          {/* OAuth Buttons — hidden for iOS + Facebook/Instagram in-app browser
              (Google blocks OAuth in these WebViews; email/OTP works reliably instead) */}
          {!hideGoogleAuth && (
            <>
              <div className="space-y-2 sm:space-y-3">
                {oauthProviders.map(({ provider, label, icon }) => (
                  <button
                    key={provider}
                    onClick={() => handleOAuthSignIn(provider)}
                    disabled={oauthLoading !== null || loading}
                    className="w-full bg-white text-[#3c4043] btn-glow py-3 min-h-[44px] rounded-button text-body font-medium hover:bg-gray-50 border border-[#dadce0] transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 sm:gap-3"
                  >
                    {oauthLoading === provider ? (
                      <div className="w-5 h-5 border-2 border-[#3c4043] border-t-transparent rounded-full animate-spin" />
                    ) : (
                      <span className="w-5 h-5 flex-shrink-0 flex items-center justify-center">{icon}</span>
                    )}
                    <span>{label}</span>
                  </button>
                ))}
              </div>

              {/* Divider */}
              <div className="flex items-center gap-3 sm:gap-4 my-4 sm:my-6">
                <div className="flex-1 h-px bg-border" />
                <span className="text-text-subtle text-body-sm">or</span>
                <div className="flex-1 h-px bg-border" />
              </div>
            </>
          )}

          {/* Email Input */}
          <div className="space-y-3 sm:space-y-4">
            <div>
              <label htmlFor="auth-email" className="block text-white text-label font-medium mb-2">
                Email address
              </label>
              <input
                id="auth-email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && email.trim() && validateEmail(email)) {
                    handleEmailContinue();
                  }
                }}
                placeholder="Enter your email address"
                className="w-full bg-bg-panel text-white text-body px-4 py-3 rounded-button border border-border focus:outline-none focus:ring-2 focus:ring-white/20 focus:border-border-hover placeholder:text-text-subtle"
                disabled={oauthLoading !== null || loading}
              />
              {/* Task 4 (v/signup-may5): typo-correction soft hint. Click to
                  accept; never blocks submission. Renders only when the
                  emailTypoCheck util returns a confident suggestion. */}
              {typoSuggestion && !loading && oauthLoading === null && (
                <p
                  className="text-xs text-white/70 mt-2"
                  data-testid="email-typo-suggestion"
                >
                  Did you mean{' '}
                  <button
                    type="button"
                    onClick={acceptTypoSuggestion}
                    className="text-accent underline underline-offset-2 hover:text-accent-hover focus:outline-none focus:ring-2 focus:ring-accent/40 rounded-sm"
                  >
                    {typoSuggestion.suggestion}
                  </button>
                  ?
                </p>
              )}
            </div>

            <button
              onClick={handleEmailContinue}
              disabled={loading || oauthLoading !== null || !email.trim() || !validateEmail(email)}
              className="w-full bg-accent text-white btn-glow px-4 sm:px-6 py-3 min-h-[44px] rounded-button text-body font-medium hover:bg-accent-hover transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {loading ? (
                <>
                  <div className="w-4 h-4 sm:w-5 sm:h-5 border-2 border-black border-t-transparent rounded-full animate-spin" />
                  <span>Loading...</span>
                </>
              ) : (
                <>
                  <span>Continue</span>
                  <svg className="w-4 h-4 sm:w-5 sm:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                  </svg>
                </>
              )}
            </button>

            {/* Error Message */}
            {error && (
              <div className="text-red-400 text-body-sm text-center mt-2">
                {error}
              </div>
            )}

            {/* Mode Toggle - Hidden on /lander-1 */}
            {!hideSignInToggleAndCancel && (
              <div className="pt-1 sm:pt-2 text-center">
                {authMode === 'signup' ? (
                  <button
                    onClick={() => {
                      posthog.capture('auth_modal_mode_toggled', {
                        from_mode: 'signup',
                        to_mode: 'signin',
                        is_facebook_browser: isFacebookBrowser,
                      });
                      setAuthMode('signin');
                    }}
                    className="text-text-subtle hover:text-white text-body-sm transition-colors underline underline-offset-2 hover:underline-offset-4 cursor-pointer"
                  >
                    Already have an account? Sign in
                  </button>
                ) : (
                  <button
                    onClick={() => {
                      posthog.capture('auth_modal_mode_toggled', {
                        from_mode: 'signin',
                        to_mode: 'signup',
                        is_facebook_browser: isFacebookBrowser,
                      });
                      setAuthMode('signup');
                    }}
                    className="text-text-subtle hover:text-white text-body-sm transition-colors underline underline-offset-2 hover:underline-offset-4 cursor-pointer"
                  >
                    Don&apos;t have an account? Sign up
                  </button>
                )}
              </div>
            )}
          </div>

          {/* Cancel Button - Hidden on /lander-1 */}
          {!hideSignInToggleAndCancel && (
            <button
              onClick={() => {
                posthog.capture('auth_modal_dismissed', {
                  time_on_modal_ms: Date.now() - modalOpenedAtRef.current,
                  attempted_method: attemptedMethod,
                  is_facebook_browser: isFacebookBrowser,
                  auth_mode: authMode,
                });
                onClose();
              }}
              className="w-full mt-4 sm:mt-6 text-text-subtle hover:text-white transition-colors text-body-sm"
            >
              Cancel
            </button>
          )}
        </div>
      </div>
    </Portal>
  );
}

